<script setup lang="ts">
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import MobileSheetGroup from "@verbleif/lib/src/components/MobileSheet/MobileSheetGroup.vue";
import MobileSheetRow from "@verbleif/lib/src/components/MobileSheet/MobileSheetRow.vue";
import VTag from "@verbleif/lib/src/components/Tag/VTag.vue";
import Avatar from "@verbleif/shared/src/components/Avatar/Avatar.vue";
import { useAccountsStore } from "@verbleif/shared/src/composables/AccountsStore/useAccountsStore";
import { useNotificationsV2 } from "@verbleif/shared/src/composables/Notifications/useNotificationsV2";
import InboxModal from "@verbleif/shared/src/composables/Notifications/v2/InboxModal.vue";
import { accountContextLine, MAX_VISIBLE_OTHER_ACCOUNTS } from "@web/features/Auth/Accounts/accountContext";
import { useAccountDialogs } from "@web/features/Auth/Accounts/useAccountDialogs";
import { useLogout } from "@web/features/Auth/Logout/useLogout";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useNavbarLocale } from "./NavbarLocaleChange/useNavbarLocale";
import NavbarNotificationsPanel from "./NavbarNotifications/NavbarNotificationsPanel.vue";
import { useProvideNavbarNotifications } from "./NavbarNotifications/useNavbarNotifications";

export type NavbarAccountView = "menu" | "language" | "notifications" | "inbox";

// The account menu of the phone sheet: the same entries as the desktop avatar dropdown in NavbarSettings.
// Language, notifications and the inbox open as views inside the sheet instead of poppers and dialogs.
const view = defineModel<NavbarAccountView>("view", { default: "menu" });

const route = useRoute();
const router = useRouter();
const accountsStore = useAccountsStore();
const { switchAccount, addAccount, reloginAccount } = useLogout();
const { openAccountsDialog, requestLogout } = useAccountDialogs();
const { t } = useI18n();
const TRANSLATION_KEY = "navbar.dropdown";

const { unreadCount, checkUnreadCount, refetch } = useProvideNavbarNotifications();
const { isV2Enabled } = useNotificationsV2();
const { languageOptions, locale } = useNavbarLocale();

const currentLanguage = computed(() => languageOptions.value.find(option => option.value === locale.value)?.label);

// The inbox belongs to the new notification module and replaces the notifications list there: switch between the two
// views when the module turns on or off, e.g. on a location switch.
watch(isV2Enabled, (enabled) => {
  if (!enabled && view.value === "inbox") {
    view.value = "menu";
  }
  if (enabled && view.value === "notifications") {
    view.value = "inbox";
  }
});

watch(view, (next) => {
  if (next === "notifications") {
    void refetch();
    checkUnreadCount();
  }
});

const accountCount = computed(() => accountsStore.accounts.value.length);
const hasMultipleAccounts = computed(() => accountCount.value > 1);
const visibleOtherAccounts = computed(() => accountsStore.otherAccounts.value.slice(0, MAX_VISIBLE_OTHER_ACCOUNTS));
const hasHiddenAccounts = computed(() => accountsStore.otherAccounts.value.length > MAX_VISIBLE_OTHER_ACCOUNTS);
const activeContext = computed(() => accountContextLine(accountsStore.activeAccount.value ?? {}));

function openOtherAccount(account: { index: number, expired?: boolean }) {
  if (account.expired) {
    reloginAccount(account.index);
    return;
  }
  switchAccount(account.index);
}

const appStores = [
  { label: "Android", url: "https://verbleif.com/android", icon: "brand-android" as const },
  { label: "iOS", url: "https://verbleif.com/ios", icon: "brand-apple" as const },
];
</script>

<template>
  <MobileSheetGroup v-if="view === 'language'">
    <MobileSheetRow
      v-for="option in languageOptions"
      :key="option.value"
      :label="option.label"
      @click="locale = option.value"
    >
      <template #icon>
        <img class="h-4 rounded-xs" :src="`/flags/${option.value.toUpperCase()}.svg`" alt="">
      </template>
      <template #trailing>
        <VIcon v-if="option.value === locale" name="check" class="text-primary-500" />
      </template>
    </MobileSheetRow>
  </MobileSheetGroup>

  <div v-else-if="view === 'notifications' && !isV2Enabled" class="h-[65dvh] mx-4 overflow-hidden rounded-2xl bg-white dark:bg-dark-800">
    <NavbarNotificationsPanel embedded />
  </div>

  <div v-else-if="view === 'inbox' && isV2Enabled" class="h-[65dvh] mx-4 overflow-hidden rounded-2xl bg-white dark:bg-dark-800 pt-4">
    <InboxModal embedded />
  </div>

  <div v-else class="flex flex-col gap-4">
    <MobileSheetGroup data-testid="mobile-accounts">
      <MobileSheetRow v-if="accountsStore.activeAccount.value" :label="accountsStore.activeAccount.value.name" data-testid="mobile-active-account">
        <template #icon>
          <Avatar :name="accountsStore.activeAccount.value.name" :avatar-url="accountsStore.activeAccount.value.avatarUrl" size="sm" />
        </template>
        <span class="flex min-w-0 flex-col">
          <span class="truncate leading-5 font-medium">{{ accountsStore.activeAccount.value.name }}</span>
          <span class="truncate text-xs leading-4 text-gray-500 dark:text-gray-400">{{ accountsStore.activeAccount.value.username }}</span>
          <span v-if="activeContext" class="truncate text-xs leading-4 text-gray-500 dark:text-gray-400">{{ activeContext }}</span>
        </span>
        <template v-if="hasMultipleAccounts" #trailing>
          <VTag variant="is-success" size="extra-small" icon-less :text="t(`${TRANSLATION_KEY}.active_tag`)" />
        </template>
      </MobileSheetRow>
      <MobileSheetRow
        v-for="account in visibleOtherAccounts"
        :key="account.index"
        :label="account.name"
        data-testid="mobile-account-item"
        @click="openOtherAccount(account)"
      >
        <template #icon>
          <Avatar :name="account.name" :avatar-url="account.avatarUrl" size="sm" />
        </template>
        <span class="flex min-w-0 flex-col">
          <span class="truncate leading-5 font-medium">{{ account.name }}</span>
          <span class="truncate text-xs leading-4 text-gray-500 dark:text-gray-400">{{ account.username }}</span>
        </span>
        <template #trailing>
          <VTag v-if="account.expired" variant="is-warning" size="extra-small" icon-less :text="t(`${TRANSLATION_KEY}.relogin_tag`)" />
          <VIcon v-else name="arrow-right-left" class="text-gray-400" aria-hidden="true" />
        </template>
      </MobileSheetRow>
      <MobileSheetRow
        v-if="hasHiddenAccounts"
        icon="users"
        :label="t(`${TRANSLATION_KEY}.all_accounts`)"
        chevron
        data-testid="mobile-all-accounts"
        @click="openAccountsDialog()"
      >
        <template #trailing>
          <span class="text-sm text-gray-400">{{ accountCount }}</span>
        </template>
      </MobileSheetRow>
      <MobileSheetRow
        icon="plus"
        :label="t(`${TRANSLATION_KEY}.add_account`)"
        data-testid="mobile-add-account"
        @click="addAccount()"
      />
    </MobileSheetGroup>

    <MobileSheetGroup>
      <MobileSheetRow
        icon="user"
        :label="t(`${TRANSLATION_KEY}.my_account`)"
        chevron
        @click="router.push('/settings/my-account')"
      />
      <MobileSheetRow
        icon="languages"
        :label="t(`${TRANSLATION_KEY}.change_language`)"
        chevron
        @click="view = 'language'"
      >
        <template #trailing>
          <span class="text-sm text-gray-400">{{ currentLanguage }}</span>
        </template>
      </MobileSheetRow>
      <MobileSheetRow
        v-if="isV2Enabled"
        icon="inbox"
        :label="t('notifications.v2.inbox.title')"
        :badge="unreadCount || null"
        chevron
        @click="view = 'inbox'"
      />
      <MobileSheetRow
        v-else
        icon="bell"
        :label="t(`${TRANSLATION_KEY}.notifications`)"
        :badge="unreadCount || null"
        chevron
        @click="view = 'notifications'"
      />
      <MobileSheetRow
        icon="map-pin"
        :label="t(`${TRANSLATION_KEY}.location_switch`)"
        chevron
        @click="router.push(`/spaces?redirect=${route.path}`)"
      />
    </MobileSheetGroup>

    <MobileSheetGroup>
      <!-- Not a MobileSheetRow: that row is a button, and the two store links inside it must be real links. -->
      <div class="flex min-h-[52px] w-full items-center gap-3 px-4 py-2 text-[15px] text-gray-800 dark:text-gray-100" data-testid="mobile-download-apps">
        <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-[15px] text-primary-500 dark:bg-primary-900/30 dark:text-primary-400" aria-hidden="true">
          <VIcon name="smartphone" />
        </span>
        <span class="min-w-0 flex-1 truncate">{{ t('navbar.download_apps') }}</span>
        <span class="flex items-center gap-1">
          <a
            v-for="store in appStores"
            :key="store.label"
            :href="store.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex size-9 items-center justify-center rounded-full text-gray-500 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden active:bg-gray-200 dark:text-gray-400 dark:active:bg-dark-600"
            :aria-label="store.label"
          >
            <VIcon :name="store.icon" aria-hidden="true" />
          </a>
        </span>
      </div>
    </MobileSheetGroup>

    <MobileSheetGroup>
      <MobileSheetRow
        variant="danger"
        icon="log-out"
        :label="t(`${TRANSLATION_KEY}.logout`)"
        data-testid="mobile-logout"
        @click="requestLogout()"
      />
    </MobileSheetGroup>
  </div>
</template>
